import React from 'react';

import styled from '@emotion/styled';

import { COLORS } from '@root/colors';

import { RateAdjustment__Upload__Product } from '@admin/schema';

interface ColumnHelpProps {
  product: RateAdjustment__Upload__Product;
}

interface InstructionsPanelProps {
  product: RateAdjustment__Upload__Product;
}

const RAILS_MAILER_PREVIEW_PATHS = {
  protectionBenefit: '/rails/mailers/mailer/account/rate_hike_with_protection_benefit',
  standard: '/rails/mailers/mailer/account/rate_hike_standard',
} as const;

const Shell = styled.div`
  background: ${COLORS.grayLightest};
  border: 1px solid ${COLORS.grayLighter};
  border-radius: 8px;
  padding: 22px 24px;
`;

const Section = styled.section`
  & + & {
    margin-top: 22px;
    padding-top: 22px;
    border-top: 1px solid ${COLORS.grayLighter};
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.tealDarker};
  line-height: 1.3;
`;

const BodyText = styled.p`
  margin: 0 0 12px;
  line-height: 1.55;
  color: ${COLORS.grayDarker};
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletList = styled.ul`
  margin: 0 0 14px;
  padding-left: 1.2rem;
  line-height: 1.55;
  color: ${COLORS.grayDarker};
  font-size: 14px;

  li {
    margin-bottom: 10px;
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const PathList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 10px 0 0;

  li + li {
    margin-top: 8px;
  }
`;

const MailerPreviewLink = styled.a`
  display: block;
  font-size: 12px;
  line-height: 1.4;
  padding: 9px 12px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.grayLighter};
  border-radius: 4px;
  word-break: break-all;
  color: ${COLORS.grayDarkest};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
    color: ${COLORS.tealDarker};
  }
`;

const InlineCode = styled.code`
  font-size: 0.92em;
  padding: 1px 5px;
  background: ${COLORS.tealLightest};
  border-radius: 3px;
  color: ${COLORS.tealDarker};
`;

const DefList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: minmax(200px, 34%) 1fr;
  gap: 14px 20px;
  font-size: 14px;
  line-height: 1.5;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 4px 0;
  }
`;

const Dt = styled.dt`
  margin: 0;
  font-weight: 600;
  color: ${COLORS.grayDarkest};

  @media (max-width: 720px) {
    margin-top: 8px;

    &:first-of-type {
      margin-top: 0;
    }
  }
`;

const Dd = styled.dd`
  margin: 0;
  color: ${COLORS.grayDark};

  @media (max-width: 720px) {
    margin-bottom: 4px;
  }
`;

const CustomerEmailSummary: React.FC = () => (
  <>
    <BodyText>
      Customers receive one of two rate-increase notices (unless <InlineCode>EXCLUDE_FROM_EMAILS</InlineCode> is{' '}
      <InlineCode>true</InlineCode>, which skips email entirely). Both include the effective date and old → new monthly
      price.
    </BodyText>
    <BodyText>
      <strong>Timing:</strong> The rate notice email is sent ~30 days before the effective date. The new monthly rate
      and the free $1k protection tier (for customers who qualify for and receive the protection-benefit notice) both
      take effect when the adjustment <strong>activates</strong> with the price increase—not when this CSV is uploaded.
    </BodyText>
    <BodyText>
      <InlineCode>INCLUDE_INSURANCE_INCENTIVE_IF_NO_INSURANCE</InlineCode> can be <InlineCode>true</InlineCode> on every
      row; we still evaluate each account when the email sends. The <strong>protection-benefit</strong> email goes to
      customers with <strong>no</strong> protection or only <strong>minimal coverage</strong> ($1/lb default) who are{' '}
      <strong>not paying</strong> for protection. Everyone else under that flag—already on $1k or $2.5k (even at $0), or
      paying for any tier—gets the <strong>standard</strong> notice. Preview and scheduling tables show a{' '}
      <strong>Rate notice email</strong> column (Excluded / Standard / Protection plan incentive). After an email is
      delivered, we store which template was used so history stays accurate if protection coverage changes later.
    </BodyText>
    <BulletList>
      <li>
        <strong>Protection-benefit variant</strong> — When the incentive column is <InlineCode>true</InlineCode>, email
        is not excluded, and the customer qualifies (above). Copy describes the free $1k offer; coverage is applied when
        the rate hike activates (see Timing).
      </li>
      <li>
        <strong>Standard rate increase</strong> — Incentive column <InlineCode>false</InlineCode>, or customer pays for
        protection, or has $1k / $2.5k+ on file (even at $0), or otherwise does not qualify for the benefit variant.
      </li>
    </BulletList>
    <BodyText>
      To view rendered HTML samples, open these paths in a browser on the same host as admin (Action Mailer previews).
      Previews are for internal review; availability depends on which environment you are using.
    </BodyText>
    <PathList>
      <li>
        <MailerPreviewLink
          href={RAILS_MAILER_PREVIEW_PATHS.protectionBenefit}
          target="_blank"
          rel="noopener noreferrer"
        >
          {RAILS_MAILER_PREVIEW_PATHS.protectionBenefit}
        </MailerPreviewLink>
      </li>
      <li>
        <MailerPreviewLink href={RAILS_MAILER_PREVIEW_PATHS.standard} target="_blank" rel="noopener noreferrer">
          {RAILS_MAILER_PREVIEW_PATHS.standard}
        </MailerPreviewLink>
      </li>
    </PathList>
  </>
);

const ColumnHelp: React.FC<ColumnHelpProps> = ({ product }) => {
  const isSelfStorage = product === RateAdjustment__Upload__Product.SelfStorage;
  return (
    <DefList>
      <Dt>
        <InlineCode>{isSelfStorage ? 'RENTAL_ID' : 'ACCOUNT_ID'}</InlineCode>
      </Dt>
      <Dd>{isSelfStorage ? 'Self-storage rental to adjust.' : 'Door-to-door storage account to adjust.'}</Dd>

      <Dt>
        <InlineCode>NEW_PRICE</InlineCode>
      </Dt>
      <Dd>
        Monthly storage rate after the increase (must be greater than the current rate; other validation rules apply).
      </Dd>

      <Dt>
        <InlineCode>EFFECTIVE_YEAR</InlineCode> / <InlineCode>EFFECTIVE_MONTH</InlineCode>
      </Dt>
      <Dd>Calendar month when the new rate takes effect (combined as MM/YYYY when parsed).</Dd>

      <Dt>
        <InlineCode>EXCLUDE_FROM_EMAILS</InlineCode>
      </Dt>
      <Dd>
        <InlineCode>true</InlineCode> skips scheduling the customer-facing rate notice email entirely (use for internal
        or exceptional cases).
      </Dd>

      {!isSelfStorage && (
        <>
          <Dt>
            <InlineCode>INCLUDE_INSURANCE_INCENTIVE_IF_NO_INSURANCE</InlineCode>
          </Dt>
          <Dd>
            <InlineCode>true</InlineCode> allows the protection-benefit notice for qualifying accounts (see Customer
            emails above); <InlineCode>false</InlineCode> uses standard copy only. When the free $1k tier applies, it is
            added in billing when the adjustment activates—see <strong>Timing</strong>.
          </Dd>
        </>
      )}

      <Dt>
        <InlineCode>BEST_OFFER</InlineCode>
      </Dt>
      <Dd>Optional numeric value stored on the scheduling for retention / ops reference.</Dd>

      <Dt>
        <InlineCode>LABEL</InlineCode> / <InlineCode>NOTES</InlineCode>
      </Dt>
      <Dd>Optional metadata for internal tracking (notes may support markdown in admin).</Dd>
    </DefList>
  );
};

export const InstructionsPanel: React.FC<InstructionsPanelProps> = ({ product }) => (
  <Shell>
    <Section>
      <SectionTitle>Customer emails</SectionTitle>
      <CustomerEmailSummary />
    </Section>
    <Section>
      <SectionTitle>Spreadsheet columns</SectionTitle>
      <ColumnHelp product={product} />
    </Section>
  </Shell>
);
